import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import AdminShell from "../components/AdminShell";
import { getAdminFxRates, isApiConfigured, putAdminFxRates } from "../apiClient";

const CURRENCY_LABELS = {
  USD: "US Dollar (default)",
  GBP: "Pound Sterling",
  EUR: "Euro",
  AUD: "Australian Dollar",
  CAD: "Canadian Dollar",
};

function toRateString(value) {
  if (value == null || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return String(n);
}

export default function AdminExchangeRatesPage({ currentUser, authToken, onSignOut }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedMessage, setSavedMessage] = useState("");
  const [baseCurrency, setBaseCurrency] = useState("USD");
  const [asOfDate, setAsOfDate] = useState("");
  const [rates, setRates] = useState([]);

  const loadRates = useCallback(async () => {
    const t = String(authToken || "").trim();
    if (!t || !isApiConfigured()) {
      setError("Not signed in.");
      setRates([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    setSavedMessage("");
    try {
      const data = await getAdminFxRates(t);
      setBaseCurrency(data.baseCurrency || "USD");
      setAsOfDate(data.asOfDate || "");
      setRates(
        Array.isArray(data.rates)
          ? data.rates.map((row) => ({
              quoteCurrency: row.quoteCurrency,
              rate: toRateString(row.rate),
            }))
          : []
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load exchange rates.");
      setRates([]);
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    void loadRates();
  }, [loadRates]);

  const isDirty = useMemo(() => rates.length > 0, [rates]);

  if (currentUser?.role !== "admin") {
    return <Navigate to="/admin/signin" replace />;
  }

  const handleRateChange = (quoteCurrency, value) => {
    setSavedMessage("");
    setRates((prev) =>
      prev.map((row) =>
        row.quoteCurrency === quoteCurrency ? { ...row, rate: value } : row
      )
    );
  };

  const handleSave = async () => {
    const t = String(authToken || "").trim();
    if (!t || saving) return;
    setSaving(true);
    setError("");
    setSavedMessage("");
    try {
      const payload = rates.map((row) => ({
        quoteCurrency: row.quoteCurrency,
        rate: Number(row.rate),
      }));
      const data = await putAdminFxRates(t, payload);
      setAsOfDate(data.asOfDate || "");
      setRates(
        Array.isArray(data.rates)
          ? data.rates.map((row) => ({
              quoteCurrency: row.quoteCurrency,
              rate: toRateString(row.rate),
            }))
          : []
      );
      setSavedMessage("Exchange rates saved.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save exchange rates.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminShell currentUser={currentUser} onSignOut={onSignOut}>
      <Link to="/admin" className="jw-admin__back">
        ← Back to home
      </Link>
      <h1 className="jw-admin__title">Exchange rates for job search</h1>
      <p className="jw-admin__lede">
        These rates convert salary filters in job search. Base currency is {baseCurrency}: 1{" "}
        {baseCurrency} equals the rate shown for each currency.
        {asOfDate ? ` Last saved for ${asOfDate}.` : ""}
      </p>

      {error ? (
        <p className="jw-admin__error" role="alert">
          {error}
        </p>
      ) : null}
      {savedMessage ? (
        <p className="jw-admin__success" role="status">
          {savedMessage}
        </p>
      ) : null}

      {loading ? <p className="jw-admin__muted">Loading exchange rates…</p> : null}

      {!loading && rates.length > 0 ? (
        <form
          className="jw-admin-rates"
          onSubmit={(event) => {
            event.preventDefault();
            void handleSave();
          }}
        >
          <div className="jw-admin-rates__table-wrap">
            <table className="jw-admin-rates__table">
              <thead>
                <tr>
                  <th scope="col">Currency</th>
                  <th scope="col">Code</th>
                  <th scope="col">
                    Rate (1 {baseCurrency} =)
                  </th>
                </tr>
              </thead>
              <tbody>
                {rates.map((row) => {
                  const isBase = row.quoteCurrency === baseCurrency;
                  return (
                    <tr key={row.quoteCurrency}>
                      <td>{CURRENCY_LABELS[row.quoteCurrency] || row.quoteCurrency}</td>
                      <td>
                        <code>{row.quoteCurrency}</code>
                      </td>
                      <td>
                        <input
                          type="number"
                          className="jw-admin-rates__input"
                          value={row.rate}
                          min="0"
                          step="0.00000001"
                          readOnly={isBase}
                          disabled={isBase}
                          aria-label={`Rate for ${row.quoteCurrency}`}
                          onChange={(event) =>
                            handleRateChange(row.quoteCurrency, event.target.value)
                          }
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="jw-admin-rates__actions">
            <button type="submit" className="jw-admin__refresh" disabled={saving || !isDirty}>
              {saving ? "Saving…" : "Save rates"}
            </button>
            <button
              type="button"
              className="jw-admin__signout"
              disabled={loading || saving}
              onClick={() => void loadRates()}
            >
              Reset
            </button>
          </div>
        </form>
      ) : null}
    </AdminShell>
  );
}
